import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Plus, Trash2, Check, X, GripVertical, WifiOff, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

const API = process.env.REACT_APP_API_URL;
const YJS_WS = API.replace(/^http/, 'ws') + '/yjs';
const PAGE_SIZE = 30;

const FEE_OPTIONS = ['무료', '유료', '공통'];
const FEE_COLORS = { 무료: 'text-emerald-400', 유료: 'text-amber-400', 공통: 'text-violet-400' };

const EMPTY_ITEM = { artist: '', company: '', manager: '', phone: '', email: '', fee: '', feeType: null, baseHours: '', note: '' };

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.startsWith('02')) {
    if (digits.length <= 6) return `${digits.slice(0,2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6)}`;
    return `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6,10)}`;
  }
  if (digits.length <= 7) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7,11)}`;
};

const formatFee = (v) => {
  if (v == null || v === '') return '';
  return Number(v).toLocaleString('ko-KR');
};

export default function PaperAdminRecruitPage() {
  const me = useSelector((s) => s.auth.me);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ─── 실시간 협업 ───
  const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({});
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider(YJS_WS, 'recruit-list', doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (provider.wsconnected) setConnected(true);
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
      profileImage: me?.profileImage || null,
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const awarenessHandler = () => {
      const myName = me?.nickName || me?.name || 'Unknown';
      const seen = new Set();
      const users = [];
      const cells = {};
      provider.awareness.getStates().forEach((state) => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
        if (state.user.editingCell && state.user.name !== myName) {
          cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
        }
      });
      const idx = users.findIndex((u) => u.isMe);
      if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      const pStr = JSON.stringify(users);
      const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc, me]);

  const notifyChange = useCallback(() => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  }, [ydoc]);

  const setEditingCell = useCallback((cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  }, [provider]);

  const EMPTY_STYLE = useMemo(() => ({}), []);
  const getCellStyle = useCallback((cellId) => {
    const peer = peerCells[cellId];
    if (!peer) return EMPTY_STYLE;
    return { boxShadow: `inset 0 0 0 2px ${peer.color}` };
  }, [peerCells, EMPTY_STYLE]);

  const CellCursor = ({ cellId }) => {
    const peer = peerCells[cellId];
    if (!peer) return null;
    return (
      <div className="absolute -top-5 left-0 z-10 pointer-events-none">
        <span className="text-[10px] font-semibold text-white px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ background: peer.color, borderRadius: '3px 3px 3px 0' }}>{peer.name}</span>
      </div>
    );
  };

  // ─── 데이터 ───
  const fetchData = useCallback(async (p = page, q = searchQ) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/recruit`, { headers, params: { q: q || undefined, page: p, limit: PAGE_SIZE } });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [headers, page, searchQ]);

  useEffect(() => { fetchData(page, searchQ); }, [page, searchQ]); // eslint-disable-line

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const doSearch = () => { setPage(1); setSearchQ(searchInput); };

  // ─── CRUD ───
  const addItem = async () => {
    if (!newItem?.artist?.trim()) return;
    try {
      const payload = { ...newItem, fee: newItem.fee ? parseInt(newItem.fee.replace(/,/g, '')) : null };
      await axios.post(`${API}/api/recruit`, payload, { headers });
      setNewItem(null);
      notifyChange();
      fetchData(page, searchQ);
    } catch { alert('오류가 발생했습니다.'); }
  };

  const updateCell = async (itemId, field, value) => {
    const payload = field === 'fee' ? (value ? parseInt(value.toString().replace(/,/g, '')) : null) : value;
    setItems((prev) => prev.map((i) => i.id === itemId ? { ...i, [field]: payload } : i));
    try {
      await axios.put(`${API}/api/recruit/${itemId}`, { [field]: payload }, { headers });
      notifyChange();
    } catch { console.error('저장 실패'); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/recruit/${itemId}`, { headers });
      notifyChange();
      fetchData(page, searchQ);
    } catch { alert('오류가 발생했습니다.'); }
  };

  const cellCls = 'border border-white/10 px-2 py-1.5 text-xs relative';
  const inputCls = 'w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5';

  return (
    <div className="bg-[#0d0b14] min-h-screen pb-20">
      {/* 헤더 */}
      <section className="pt-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-white text-2xl font-bold">섭외리스트</h1>
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex -space-x-1.5">
                {peers.slice(0, 6).map((p, i) => (
                  <div key={i}
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                    style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }}
                    title={p.isMe ? `${p.name} (나)` : p.name}>
                    {p.profileImage ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" /> : p.name?.charAt(0)}
                  </div>
                ))}
              </div>
              {connected
                ? <div className="w-2 h-2 rounded-full bg-emerald-400" title="실시간 연결됨" />
                : <WifiOff size={14} className="text-red-400" title="오프라인" />}
            </div>
          </div>
          <p className="text-white/40 text-sm mt-1">총 {total}건</p>
        </div>
      </section>

      {/* 검색 */}
      <section className="pb-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 max-w-sm">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                placeholder="아티스트명 검색"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-white/25 outline-none focus:border-violet-400/40"
              />
            </div>
            <button onClick={doSearch} className="px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 text-xs rounded-lg transition-colors">
              검색
            </button>
            {searchQ && (
              <button onClick={() => { setSearchInput(''); setSearchQ(''); setPage(1); }}
                className="px-3 py-2 text-white/30 hover:text-white/60 text-xs transition-colors">
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* 테이블 */}
      <section>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white/[0.04]">
                  <th className={`${cellCls} w-8 text-white/20`}></th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[120px]`}>아티스트</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[120px]`}>소속사</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[100px]`}>담당자</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[130px]`}>전화번호</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[160px]`}>이메일</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium w-24`}>금액(만원)</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium w-20`}>유무료</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium w-24`}>기준시간</th>
                  <th className={`${cellCls} text-center text-white/60 font-medium min-w-[160px]`}>비고</th>
                  <th className={`${cellCls} w-14 text-white/20`}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="py-16 text-center text-white/20 text-xs">로딩 중...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={11} className="py-16 text-center text-white/20 text-xs">항목이 없습니다.</td></tr>
                ) : items.map((item) => (
                  <tr key={item.id} className="hover:bg-white/[0.02] transition-colors">
                    {/* 드래그 핸들 (정렬시 사용 안하지만 UI 유지) */}
                    <td className={`${cellCls} text-center text-white/20`}>
                      <GripVertical size={12} />
                    </td>
                    {/* 아티스트 */}
                    <td className={cellCls} style={getCellStyle(`artist-${item.id}`)}>
                      <CellCursor cellId={`artist-${item.id}`} />
                      <input value={item.artist || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, artist: e.target.value } : i))}
                        onBlur={(e) => { updateCell(item.id, 'artist', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`artist-${item.id}`)}
                        className={`${inputCls} font-medium`} placeholder="아티스트명" />
                    </td>
                    {/* 소속사 */}
                    <td className={cellCls} style={getCellStyle(`company-${item.id}`)}>
                      <CellCursor cellId={`company-${item.id}`} />
                      <input value={item.company || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, company: e.target.value } : i))}
                        onBlur={(e) => { updateCell(item.id, 'company', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`company-${item.id}`)}
                        className={inputCls} placeholder="소속사명" />
                    </td>
                    {/* 담당자 */}
                    <td className={cellCls} style={getCellStyle(`manager-${item.id}`)}>
                      <CellCursor cellId={`manager-${item.id}`} />
                      <input value={item.manager || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, manager: e.target.value } : i))}
                        onBlur={(e) => { updateCell(item.id, 'manager', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`manager-${item.id}`)}
                        className={inputCls} placeholder="담당자명" />
                    </td>
                    {/* 전화번호 */}
                    <td className={cellCls} style={getCellStyle(`phone-${item.id}`)}>
                      <CellCursor cellId={`phone-${item.id}`} />
                      <input value={item.phone || ''}
                        onChange={(e) => { const v = formatPhone(e.target.value); setItems((p) => p.map((i) => i.id === item.id ? { ...i, phone: v } : i)); }}
                        onBlur={(e) => { updateCell(item.id, 'phone', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`phone-${item.id}`)}
                        inputMode="numeric" className={inputCls} placeholder="010-0000-0000" />
                    </td>
                    {/* 이메일 */}
                    <td className={cellCls} style={getCellStyle(`email-${item.id}`)}>
                      <CellCursor cellId={`email-${item.id}`} />
                      <input value={item.email || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, email: e.target.value } : i))}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { alert('이메일 형식이 올바르지 않습니다.'); return; }
                          updateCell(item.id, 'email', v); setEditingCell(null);
                        }}
                        onFocus={() => setEditingCell(`email-${item.id}`)}
                        className={inputCls} placeholder="example@email.com" />
                    </td>
                    {/* 금액 */}
                    <td className={cellCls} style={getCellStyle(`fee-${item.id}`)}>
                      <CellCursor cellId={`fee-${item.id}`} />
                      <input
                        value={item.fee != null ? formatFee(item.fee) : ''}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setItems((p) => p.map((i) => i.id === item.id ? { ...i, fee: raw ? parseInt(raw) : null } : i));
                        }}
                        onBlur={(e) => { updateCell(item.id, 'fee', item.fee); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`fee-${item.id}`)}
                        inputMode="numeric" className={`${inputCls} text-right`} placeholder="0" />
                    </td>
                    {/* 유무료 */}
                    <td className={cellCls} style={getCellStyle(`feeType-${item.id}`)}>
                      <CellCursor cellId={`feeType-${item.id}`} />
                      <select value={item.feeType || ''}
                        onChange={(e) => { const v = e.target.value || null; updateCell(item.id, 'feeType', v); }}
                        onFocus={() => setEditingCell(`feeType-${item.id}`)}
                        onBlur={() => setEditingCell(null)}
                        className={`${inputCls} text-center ${FEE_COLORS[item.feeType] || 'text-white/40'}`}>
                        <option value="">-</option>
                        {FEE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    {/* 기준시간 */}
                    <td className={cellCls} style={getCellStyle(`hours-${item.id}`)}>
                      <CellCursor cellId={`hours-${item.id}`} />
                      <input value={item.baseHours || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, baseHours: e.target.value } : i))}
                        onBlur={(e) => { updateCell(item.id, 'baseHours', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`hours-${item.id}`)}
                        className={`${inputCls} text-center`} placeholder="예: 60분" />
                    </td>
                    {/* 비고 */}
                    <td className={cellCls} style={getCellStyle(`note-${item.id}`)}>
                      <CellCursor cellId={`note-${item.id}`} />
                      <input value={item.note || ''}
                        onChange={(e) => setItems((p) => p.map((i) => i.id === item.id ? { ...i, note: e.target.value } : i))}
                        onBlur={(e) => { updateCell(item.id, 'note', e.target.value); setEditingCell(null); }}
                        onFocus={() => setEditingCell(`note-${item.id}`)}
                        className={inputCls} placeholder="비고" />
                    </td>
                    {/* 삭제 */}
                    <td className={`${cellCls} text-center`}>
                      <button onClick={() => deleteItem(item.id)} className="p-1 text-white/20 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}

                {/* 추가 행 */}
                {newItem ? (
                  <tr className="bg-white/[0.02]">
                    <td className={cellCls}></td>
                    <td className={cellCls}>
                      <input value={newItem.artist} onChange={(e) => setNewItem((p) => ({ ...p, artist: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                        className={`${inputCls} font-medium`} placeholder="아티스트명*" autoFocus />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.company} onChange={(e) => setNewItem((p) => ({ ...p, company: e.target.value }))}
                        className={inputCls} placeholder="소속사명" />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.manager} onChange={(e) => setNewItem((p) => ({ ...p, manager: e.target.value }))}
                        className={inputCls} placeholder="담당자명" />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.phone} onChange={(e) => setNewItem((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
                        inputMode="numeric" className={inputCls} placeholder="010-0000-0000" />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.email} onChange={(e) => setNewItem((p) => ({ ...p, email: e.target.value }))}
                        className={inputCls} placeholder="example@email.com" />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.fee} onChange={(e) => setNewItem((p) => ({ ...p, fee: e.target.value.replace(/[^0-9]/g, '') }))}
                        inputMode="numeric" className={`${inputCls} text-right`} placeholder="0" />
                    </td>
                    <td className={cellCls}>
                      <select value={newItem.feeType || ''} onChange={(e) => setNewItem((p) => ({ ...p, feeType: e.target.value || null }))}
                        className={`${inputCls} text-center`}>
                        <option value="">-</option>
                        {FEE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.baseHours} onChange={(e) => setNewItem((p) => ({ ...p, baseHours: e.target.value }))}
                        className={`${inputCls} text-center`} placeholder="예: 60분" />
                    </td>
                    <td className={cellCls}>
                      <input value={newItem.note} onChange={(e) => setNewItem((p) => ({ ...p, note: e.target.value }))}
                        className={inputCls} placeholder="비고" />
                    </td>
                    <td className={`${cellCls} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={addItem} className="p-1 text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                        <button onClick={() => setNewItem(null)} className="p-1 text-white/30 hover:text-white"><X size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={11} className={`${cellCls} text-center`}>
                      <button onClick={() => setNewItem({ ...EMPTY_ITEM })}
                        className="flex items-center justify-center gap-1.5 w-full py-1 text-white/30 hover:text-white/60 text-xs transition-colors">
                        <Plus size={13} /> 섭외 추가
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이징 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all">
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-xs transition-all ${
                    p === page ? 'bg-violet-400/20 text-violet-400 border border-violet-400/30' : 'text-white/40 hover:text-white/60'
                  }`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all">
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
